// ─── Field Validators ─────────────────────────────────────────────────
// Reusable validation functions for common field types

export const validators = {
  /* Required field - returns error message or null */
  required: (value, fieldName = 'This field') => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },

  /* Email format */
  email: (value) => {
    if (!value) return null;
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(value) ? null : 'Please enter a valid email address';
  },

  /* US Phone number - accepts (XXX) XXX-XXXX, XXX-XXX-XXXX, XXXXXXXXXX */
  phone: (value) => {
    if (!value) return null;
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 10) return 'Phone number must be 10 digits';
    return null;
  },

  /* US Zip code - 5 digits or 5+4 format */
  zip: (value) => {
    if (!value) return null;
    const re = /^\d{5}(-\d{4})?$/;
    return re.test(value) ? null : 'Zip code must be 5 digits or 5+4 format (e.g. 78702 or 78702-1234)';
  },

  /* State - 2 letter US state code */
  state: (value) => {
    if (!value) return null;
    const states = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
    const upper = value.toUpperCase();
    if (upper.length !== 2) return 'State must be a 2-letter code (e.g. TX)';
    if (!states.includes(upper)) return 'Please enter a valid US state code';
    return null;
  },

  /* Tax ID - XX-XXXXXXX format */
  taxId: (value) => {
    if (!value) return null;
    const re = /^\d{2}-?\d{7}$/;
    return re.test(value) ? null : 'Tax ID must be 9 digits (XX-XXXXXXX)';
  },

  /* NPI - 10 digit National Provider Identifier */
  npi: (value) => {
    if (!value) return null;
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 10) return 'NPI must be exactly 10 digits';
    return null;
  },

  /* Member ID - format SND-XXXXXX */
  memberId: (value) => {
    if (!value) return null;
    const re = /^[A-Z]{2,4}-?\d{5,9}$/i;
    return re.test(value) ? null : 'Member ID format invalid (e.g. SND-100234)';
  },

  /* Date - must be valid date */
  date: (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? 'Please enter a valid date' : null;
  },

  /* Future date */
  futureDate: (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Please enter a valid date';
    if (d < new Date().setHours(0,0,0,0)) return 'Date must be in the future';
    return null;
  },

  /* Past date (e.g. DOB) */
  pastDate: (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Please enter a valid date';
    if (d > new Date()) return 'Date cannot be in the future';
    return null;
  },

  /* Password strength */
  password: (value) => {
    //if (!value) return null;
    if (!value) return "Password is required";
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
    if (!/\d/.test(value)) return 'Password must contain a number';
    return null;
  },

  /* Numeric only */
  numeric: (value) => {
    if (!value) return null;
    return /^\d+$/.test(value) ? null : 'Only numbers are allowed';
  },

  /* Alpha only */
  alpha: (value) => {
    if (!value) return null;
    return /^[a-zA-Z\s'-]+$/.test(value) ? null : 'Only letters, spaces, hyphens, and apostrophes allowed';
  },

  /* Max length */
  maxLength: (max) => (value) => {
    if (!value) return null;
    return value.length > max ? `Maximum ${max} characters` : null;
  },

  /* Min length */
  minLength: (min) => (value) => {
    if (!value) return null;
    return value.length < min ? `Minimum ${min} characters` : null;
  },

  /* Procedure Code (CPT) - 5 digit */
  procedureCode: (value) => {
    if (!value) return null;
    return /^\d{5}$/.test(value) ? null : 'CPT code must be 5 digits';
  },

  /* Diagnosis Code (ICD-10) - letter followed by digits */
  diagnosisCode: (value) => {
    if (!value) return null;
    return /^[A-Z]\d{2}(\.\d{1,4})?$/i.test(value) ? null : 'Invalid ICD-10 format (e.g. G43.909)';
  },

  /* Check number */
  checkNumber: (value) => {
    if (!value) return null;
    return /^[A-Z0-9-]{4,20}$/i.test(value) ? null : 'Invalid check number format';
  },
};

/* ── Formatters ─────────────────────────────────────────────────── */
// Auto-format as user types

export const formatters = {
  /* Format phone as (XXX) XXX-XXXX */
  phone: (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  },

  /* Format zip as XXXXX or XXXXX-XXXX */
  zip: (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  },

  /* Format tax ID as XX-XXXXXXX */
  taxId: (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  },

  /* Uppercase state */
  state: (value) => value ? value.toUpperCase().slice(0, 2) : '',

  /* Numeric only */
  numeric: (value) => value ? value.replace(/\D/g, '') : '',

  /* Uppercase */
  uppercase: (value) => value ? value.toUpperCase() : '',
};

/* ── Helper: validate form object against rules ──────────────────── */
// Usage: validateForm({ email: 'x@y.com', phone: '555' }, { email: [validators.required, validators.email], phone: [validators.phone] })
// Returns { email: null, phone: 'Phone number must be 10 digits' }

export const validateForm = (values, rules) => {
  const errors = {};
  Object.keys(rules).forEach(field => {
    const fieldRules = Array.isArray(rules[field]) ? rules[field] : [rules[field]];
    for (const rule of fieldRules) {
      const error = rule(values[field], field);
      if (error) {
        errors[field] = error;
        break;
      }
    }
    if (!errors[field]) errors[field] = null;
  });
  return errors;
};

export const hasErrors = (errors) => Object.values(errors).some(e => e !== null);
